import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  selectAccessToken,
  selectBannersCreateBanner,
  selectPassedTestsCreateBanner,
} from '../routing/fetchingfunction/user/user.reducer'
import { getBanners, getTutorTestRecords } from '../routing/fetchingfunction/user/user.action'

import useMainCategories from './useMainCategories'

/**
 * @param {'banner' | 'passed'} availability
 */
const useAvailableCategories = (availability = 'banner') => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const accessToken = useSelector(selectAccessToken)

  const passedTests = useSelector(selectPassedTestsCreateBanner)
  const banners = useSelector(selectBannersCreateBanner)

  const mainCategories = useMainCategories()

  const data = useMemo(() => {
    const _passedTest = {}
    passedTests.forEach((test) => {
      const key = test.category?.slug ?? test.category?.category_slug ?? test.category_slug
      if (_passedTest[key]) {
        const x_date = new Date(_passedTest[key].test.next_available_date)
        const y_date = new Date(test.test.next_available_date)
        if (y_date > x_date) {
          if (
            (_passedTest[key].test.is_passed && !test.test.is_passed) ||
            (_passedTest[key].test.level === 'intermediate' && test.test.level === 'beginner')
          ) {
            return
          }
          _passedTest[key] = { ...test, taken: true }
        }
      } else {
        _passedTest[key] = test
      }
    })

    const sortedTest = Object.values(_passedTest)

    if (availability === 'passed') {
      const passedCategories = passedTests
        .filter((passedTest) => passedTest.test?.is_passed)
        .map((passedTest) => passedTest.category)

      if (typeof passedCategories[0] !== 'object') {
        return {
          passedCategories: [],
          formattedPassedCategories: [],
          sortedTest: [],
        }
      }

      const formattedPassedCategories = passedCategories.map((e) => ({
        label: e.name,
        value: e.id,
      }))

      return {
        sortedTest: sortedTest,

        passedCategories,
        formattedPassedCategories,
        formattedClassBannerCategories: formattedPassedCategories,
      }
    }

    const passedCategories = passedTests
      .filter((passedTest) => passedTest.test?.is_passed)
      .map((passedTest) => passedTest.category)

    const allCategories = passedTests.map((passedTest) => {
      return { ...passedTest.category, is_passed: passedTest.test?.is_passed }
    })

    if (typeof passedCategories[0] !== 'object') {
      return {
        classBannerCategories: [],
        formattedClassBannerCategories: [],
        allCategories: [],
        sortedTest: [],
      }
    }

    const availableCategories = passedCategories.filter(
      (test) =>
        banners.findIndex((banner) => test?.slug === banner.class_banner_category?.slug) === -1
    )

    const normalizedClassBannerCategories = mainCategories.filter(
      (category) =>
        availableCategories.findIndex(
          (availableCategory) =>
            (availableCategory?.slug ?? availableCategory?.category_slug) === category?.slug
        ) !== -1
    )
    const formattedClassBannerCategories = normalizedClassBannerCategories.map((e) => ({
      label: e.name,
      value: e.id,
    }))

    const formattedClassBannersCategories = banners.map((e) => ({
      label: e.class_banner_category.name,
      value: e.class_banner_category.id,
    }))

    return {
      classBannerCategories: normalizedClassBannerCategories,
      formattedClassBannerCategories,
      allCategories,
      sortedTest: sortedTest,
      formattedBanners: formattedClassBannersCategories,
    }
  }, [passedTests, banners, mainCategories])

  useEffect(() => {
    if (accessToken) {
      setLoading(true)
      if (availability === 'banner') {
        dispatch(getBanners())
          .unwrap()
          .finally(() => setLoading(false))
      }

      if (!passedTests.length) {
        dispatch(getTutorTestRecords())
          .unwrap()
          .finally(() => setLoading(false))
      }
    } else {
      setLoading(false)
    }
  }, [accessToken])
  return {
    loading,
    banners: banners,
    formattedBanners: data.formattedBanners,
    formattedClassBannerCategories: data?.formattedClassBannerCategories,
    loading,
    passedCategories: data?.passedCategories,
    classBannerCategories: data?.classBannerCategories,
    allCategories: data?.allCategories,
    takenTests: data.sortedTest,
  }
}

export default useAvailableCategories
